import { IconPlus, IconTrash } from '@tabler/icons-react';
import type { EnvironmentVariable } from '@xver/apis/v1/kufer/containers/model';
import Button from '@xver/components/Atoms/Button/Button';
import InputField from '@xver/components/Atoms/InputField/InputField';
import { useTranslation } from 'react-i18next';

interface EnvironmentVariablesEditorProps {
    value: EnvironmentVariable[];
    onChange: (value: EnvironmentVariable[]) => void;
}

export default function EnvironmentVariablesEditor({
    value,
    onChange,
}: EnvironmentVariablesEditorProps) {
    const { t } = useTranslation('containerEngine');
    const updateAt = (index: number, patch: Partial<EnvironmentVariable>) => {
        const next = value.map((item, i) => (i === index ? { ...item, ...patch } : item));
        onChange(next);
    };

    const removeAt = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
    };

    const addRow = () => {
        onChange([...value, { name: '', value: '' }]);
    };

    return (
        <div className="w-full flex flex-col gap-3">
            {value.length === 0 && (
                <p className="text-sm text-fg/68">
                    {t(
                        'deployment.envVars.empty',
                        'No environment variables set. Add one to override defaults at runtime.'
                    )}
                </p>
            )}
            {value.map((row, index) => (
                <div key={index} className="w-full grid grid-cols-[1fr_2fr_auto] gap-2 items-start">
                    <InputField
                        size="md"
                        placeholder={t('deployment.envVars.namePlaceholder', 'NAME')}
                        aria-label={t('deployment.envVars.nameAria', 'Variable name')}
                        value={row.name}
                        onChange={(e) => updateAt(index, { name: e.target.value.toUpperCase() })}
                        pattern="[A-Za-z_][A-Za-z0-9_]*"
                    />
                    <InputField
                        size="md"
                        placeholder={t('deployment.envVars.valuePlaceholder', 'value')}
                        aria-label={t('deployment.envVars.valueAria', 'Variable value')}
                        value={row.value}
                        onChange={(e) => updateAt(index, { value: e.target.value })}
                    />
                    <button
                        type="button"
                        aria-label={t('deployment.envVars.removeAria', 'Remove variable')}
                        onClick={() => removeAt(index)}
                        className="h-[2.35rem] px-2 inline-flex items-center justify-center rounded-md border border-line bg-surface-2 text-fg/68 hover:text-fg hover:bg-surface-1 transition-colors"
                    >
                        <IconTrash size={16} />
                    </button>
                </div>
            ))}
            <div>
                <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<IconPlus size={16} />}
                    onClick={addRow}
                >
                    {t('deployment.envVars.addButton', 'Add variable')}
                </Button>
            </div>
        </div>
    );
}
